import { formatPhoneNumber } from "@gaiaProfessionals/Common/Utilities/formatPhoneNumber";
import { CallbackType, ErrorObjectType, InputObjectType, validate } from "@gaiaProfessionals/Common/Utilities/validate";
import GAIASignInRequiredToBrowseWrapper from "@gaiaProfessionals/Components/HCP/GAIASignInRequiredToBrowseWrapper";
import {
    FormErrorsStateType,
    intialErrorFormState,
    intialErrorFormStateNonDefault,
    newCustomerValidatorConfig,
    practiceTypeOptions,
    ValidatorConfigType,
} from "@gaiaProfessionals/Components/HCP/HCPUpdateAccountNewFormValidatorConfig";
import updateAccount from "@gaiaProfessionals/Store/Accounts/Handlers/HCPUpdateAccount";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Plus from "@insite/mobius/Icons/Plus";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl: getCreateAccountReturnUrl(state),
        countries: getCurrentCountries(state),
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        billToState: getBillToState(state, state.context.session.billToId),
        session: state.context.session,
        certificateUploadForDefaultCustomer:
            state.context.settings.properties.hcpUserCertificateUploadForDefaultCustomer,
        currentLocation: getLocation(state),
    };
};

const mapDispatchToProps = {
    updateAccount,
    signIn,
    validatePassword,
    loadCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    // validateReCaptcha: makeHandlerChainAwaitable<{}, boolean>(validateReCaptcha),
    loadBillTo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface UpdateHCPAccountDetailStyles {
    form?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    GridItemWidth?: GridItemProps;
    rightSectionGridItem?: GridItemProps;
    lastNameTextField?: TextFieldPresentationProps;
    practitionerTypeGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    uploadTaxGridItem?: GridItemProps;
    fileUploader?: FileUploadPresentationProps;
    uploadLicenseGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    extGridItem?: GridItemProps;
    shipTitleGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    noteWrapper?: ButtonPresentationProps;
    noteBold?: TypographyProps;
    noteLight?: TypographyProps;
    alertContainer?: InjectableCss;
    alertText?: TypographyProps;
    heading?: TypographyProps;
    indentationGridItem?: GridItemProps;
    redLinks?: TypographyProps;
    addIconWrapper?: TypographyProps;
    addIcon?: TypographyProps;
    generalHeading?: TypographyProps;
    headingWrapper?: TypographyProps;
}
export const updateHCPAccountDetailStyles: UpdateHCPAccountDetailStyles = {
    mainGridContainer: {
        css: css`
            > ${GridOffset} {
                justify-content: center;
                margin: 20px 0 40px 0;
                width: 100%;
                display: flex;
                > ${GridItemStyle} {
                    flex-basis: 100%;
                }
            }
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            max-width: 100% !important;
            padding: 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding: 20px 20px;
                    `,
                    css`
                        width: 100%;
                        padding: 20px 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    phoneNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            padding-right: 15px;
        `,
    },
    userGridContainer: {
        css: css`
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fullWidthGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css``,
    },
    countryGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    GridItemWidth: {
        width: [12, 12, 12, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    practitionerTypeGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    uploadTaxGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
            > div[class^="UploadWrapper"] {
                width: 100%;
                display: flex;
                align-items: flex-start;
                position: relative;
            }
        `,
    },
    shipTitleGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        // margin-top: 10px !important;
                    `,
                    css`
                        // margin-top: 10px !important;
                    `,
                    css`
                        // margin-top: 10px !important;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    rightSectionGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    extGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        // margin-top: 10px !important;
                    `,
                    css`
                        // margin-top: 10px !important;
                    `,
                    css`
                        // margin-top: 10px !important;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    uploadLicenseGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
            > div[class^="UploadWrapper"] {
                width: 100%;
                display: flex;
                align-items: flex-start;
            }
        `,
    },
    fileUploader: {
        cssOverrides: {
            formField: css`
                > span {
                    position: absolute;
                    width: 260px;
                }
            `,
            formInputWrapper: css`
                margin-top: 28px !important;
                > div[class^="FileUploadStyle"] {
                    > div[class^="AppearanceTransition"] {
                        > button {
                            top: -1px;
                            right: 33px;
                        }
                    }
                }
            `,
        },
        buttonProps: {
            variant: "secondary",
            css: css`
                border-radius: 10px;
                color: ${getColor("common.accent")};
                margin: 27px 0 0 8px;
                padding: 0 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )};
            `,
        },
    },
    alertContainer: {
        css: css`
            color: ${getColor("warning")};
            display: flex;
            margin-top: 5px;
            svg {
                width: 14px;
                height: 13px;
            }
        `,
    },
    alertText: {
        css: css`
            margin-left: 7px;
            font-size: 0.875rem;
        `,
    },
    submitButton: {
        css: css`
            border-radius: 10px;
            min-height: 44px;
            min-width: 138px;
        `,
    },
    noteWrapper: {
        css: css`
            color: ${getColor("primary")};
            font-size: 0.875rem;
            margin-top: 7px;
        `,
    },
    noteBold: {
        fontFamily: "LatoBold",
        css: css`
            margin-right: 3px;
        `,
    },
    heading: {
        color: "#1A1A1A",
        size: 18,
        fontFamily: "LatoBold",
    },
    indentationGridItem: {
        css: css`
            display: none;
        `,
    },
    redLinks: {
        css: css`
            color: #d14124;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            font-family: "LatoBold";
            display: flex;
            margin-left: 7px;
        `,
    },
    addIconWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
        `,
    },
    addIcon: {
        css: css`
            height: 10px;
            width: 10px;
            color: #d14124;
            display: flex;
            > svg {
                height: 10px;
                width: 10px;
            }
        `,
    },
    generalHeading: {
        css: css`
            color: #205c40;
            font-size: 22px;
            font-weight: unset;
            font-family: "LibreBaskerville";
        `,
    },
    headingWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: baseline;
        `,
    },
};

const styles = updateHCPAccountDetailStyles;
const StyledForm = getStyledWrapper("form");
const stateRegexp = /^[A-Za-z]+$/;

const UpdateHCPAccountDetail = ({
    updateAccount,
    validateReCaptcha,
    loadCurrentCountries,
    accountSettings,
    shouldLoadCountries,
    countries,
    history,
    signInUrl,
    billToState,
    session,
    loadBillTo,
    certificateUploadForDefaultCustomer,
    currentLocation,
}: Props) => {
    useEffect(() => {
        if (!session.isAuthenticated) {
            const signIn = signInUrl || "SignIn";
            const returnUrl = currentLocation.pathname + currentLocation.search;
            window.location.href = `${signIn}?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
    }, []);
    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && session.billToId && session.isAuthenticated) {
            loadBillTo({ billToId: session.billToId });
        }
    }, [billToState]);

    const countryRequiredFieldMessage = siteMessage("Country is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone number is Required");
    const stateRequiredFieldMessage = siteMessage("State is Required");

    const practionerTypeRequiredFieldMessage = siteMessage("Practioner Type is Required");
    const questionRequiredFieldMessage = siteMessage("This field is Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [inValidFile, setInValidFile] = React.useState(false);
    const [businessName, setBusinessName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [taxIdEin, settaxIdEin] = useState("");
    const [currentDba, setCurrentDba] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [otherState, setOtherState] = useState("");

    const [countryError, setCountryError] = useState(
        accountSettings.useEmailAsUserName ? "" : countryRequiredFieldMessage,
    );
    const [stateError, setStateError] = useState(accountSettings.useEmailAsUserName ? "" : stateRequiredFieldMessage);
    const [phoneNumberError, setPhoneNumberError] = useState(
        accountSettings.useEmailAsUserName ? "" : phoneNumberRequiredFieldMessage,
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);

    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);

    const toasterContext = useContext(ToasterContext);
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [address3, setAddress3] = useState("");
    const [practiceName, setPracticeName] = useState("");
    const [practiceType, setPracticeType] = useState("");

    // Shipping Address
    const [isShipAddress, setIsShipAddress] = useState(false);

    const [shipCity, setShipCity] = useState("");
    const [shipPostalCode, setShipPostalCode] = useState("");
    const [shipOtherState, setShipOtherState] = useState("");
    const [shipSelectedCountry, setShipSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [shipSelectedState, setShipSelectedState] = useState<StateModel | undefined>(undefined);
    const [shipAddress1, setShipAddress1] = useState("");
    const [shipAddress2, setShipAddress2] = useState("");
    const [shipAddress3, setShipAddress3] = useState("");
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const [shipStateValidationError, setShipStateValidationError] = useState(false);

    // End Shipping Address

    const fileUploadTaxCertificateRef = useRef({ value: "" } as HTMLInputElement);
    const fileUploadPartitionerLicenceRef = useRef({ value: "" } as HTMLInputElement);
    const [taxFile, setTaxFile] = React.useState<any>(null);
    const [partitionerLicenseFile, setPartitionerLicenseFile] = React.useState<any>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);
    const [fileSizeExceeded, setFileSizeExceeded] = React.useState(false);
    const [taxSizeExceeded, setTaxSizeExceeded] = React.useState(false);
    const [incorrectExtension, setIncorrectExtension] = React.useState(false);
    const [practionerTypeError, setPractionerTypeError] = useState(practionerTypeRequiredFieldMessage);
    const [stateValidationError, setStateValidationError] = useState(false);
    const { useEmailAsUserName } = accountSettings;
    React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
        if (billToState?.value?.customerNumber !== certificateUploadForDefaultCustomer) {
            setFormErrors(intialErrorFormStateNonDefault);
        }
    }, []);

    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    React.useEffect(() => {
        if (incorrectFileExtension || incorrectExtension) {
            setInValidFile(true);
        } else {
            setInValidFile(false);
        }
    }, [incorrectExtension, incorrectFileExtension]);

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const validateManaulHandler = (name: string, value: string) => {
        setFormErrors({
            ...formErrors,
            [name]: value,
        });
    };

    const validateHandler = (
        input: HTMLInputElement | HTMLSelectElement | any,
        customValidation?: CallbackType,
    ): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig?.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const handelEmptyStateAfterCountryChanged = () => {
        setSelectedState(undefined);
        setOtherState("");
        setFormErrors({ ...formErrors, state: "" });
    };

    const handelShipEmptyStateAfterCountryChanged = () => {
        setShipSelectedState(undefined);
        setShipOtherState("");
        setFormErrors({ ...formErrors, shipState: "" });
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        validateHandler(event.target);
        setSelectedCountry(country);
        handelEmptyStateAfterCountryChanged();
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setSelectedState(state);
    };

    const businessNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBusinessName(input.value);
    };
    const phoneNumberChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneNumber(formatPhoneNumber(input.value));
    };
    const taxIdEinChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        settaxIdEin(input.value);
    };
    const currentDbaChangeHandler = (input: HTMLInputElement) => {
        // validateHandler(input);
        setCurrentDba(input.value);
    };
    const address1ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setAddress1(input.value);
    };
    const address2ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setAddress2(input.value);
    };
    const address3ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setAddress3(input.value);
    };

    // Shiping Handlers
    const validateShippingAddress = () => {
        formErrors.shipCity = "";
        formErrors.shipPostalCode = "";
        formErrors.shipState = "";
        formErrors.shipAddress1 = "";
        formErrors.shipCountry = "";
        setFormErrors({
            ...formErrors,
        });
    };

    const removeValidateShippingAddress = () => {
        delete formErrors.shipCity;
        delete formErrors.shipPostalCode;
        delete formErrors.shipState;
        delete formErrors.shipAddress1;
        delete formErrors.shipCountry;

        // make empty Shiping addrress filed
        setShipOtherState("");
        setShipSelectedCountry(undefined);
        setShipCity("");
        setShipPostalCode("");
        setShipAddress1("");
        setShipAddress2("");
        setShipAddress3("");
        // end

        setFormErrors({
            ...formErrors,
        });
    };
    const handleisShipAddress = () => {
        if (!isShipAddress) {
            validateShippingAddress();
        } else {
            removeValidateShippingAddress();
        }
        setIsShipAddress(prev => !prev);
    };

    const handleShipCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        validateHandler(event.target);
        setShipSelectedCountry(country);
        // handelEmptyStateAfterCountryChanged();
        handelShipEmptyStateAfterCountryChanged();
    };

    const handleShipStateChange: SelectProps["onChange"] = event => {
        const state = shipSelectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setShipSelectedState(state);
    };

    const shipOtherStateChangeHandler = (input: HTMLInputElement) => {
        const regex = !stateRegexp.test(input.value);
        setStateValidationError(regex);
        validateHandler(input);
        setShipOtherState(input.value);
    };

    const shipCityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setShipCity(input.value);
    };
    const shipPostalCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setShipPostalCode(input.value);
    };

    const shipAddress1ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setShipAddress1(input.value);
    };
    const shipAddress2ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setShipAddress2(input.value);
    };
    const shipAddress3ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setShipAddress3(input.value);
    };

    // End Shiping Handlers

    const practiceNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPracticeName(input.value);
    };
    const practionerTypeChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setPracticeType(event.target.value);
    };

    const otherStateChangeHandler = (input: HTMLInputElement) => {
        const regex = !stateRegexp.test(input.value);
        setStateValidationError(regex);
        validateHandler(input);
        setOtherState(input.value);
    };
    const cityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCity(input.value);
    };
    const postalCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPostalCode(input.value);
    };

    const getFileExtension = (filename: string) => {
        return filename.split(".").pop()?.toLowerCase();
    };

    const validateFileSize = (file: File) => {
        const maxSize = 5 * 1024 * 1024; // 5 MB in bytes

        if (file.size > maxSize) {
            return false;
        }
        return true;
    };

    const taxFileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setTaxFile(uploadedFile);
        if (uploadedFile) {
            if (!validateFileSize(uploadedFile)) {
                // setUploadLimitExceeded({ uploadLimitExceeded: true });
                setTaxSizeExceeded(true);
            } else {
                setTaxSizeExceeded(false);
                const fileExtension = getFileExtension(uploadedFile.name) as string;
                const incorrectExtension = ["pdf", "jpg", "png"].indexOf(fileExtension) === -1;
                setIncorrectFileExtension(incorrectExtension);
                setIsBadFile({ isBadFile: incorrectExtension });
                setUploadLimitExceeded({ uploadLimitExceeded: false });
            }
        }
    };

    const credentialsFileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setPartitionerLicenseFile(uploadedFile);
        if (uploadedFile) {
            if (!validateFileSize(uploadedFile)) {
                setFileSizeExceeded(true);
            } else {
                setFileSizeExceeded(false);
                const fileExtension = getFileExtension(uploadedFile.name) as string;
                const incorrectExtension = ["pdf", "jpg", "png"].indexOf(fileExtension) === -1;
                setIncorrectExtension(incorrectExtension);
                setIsBadFile({ isBadFile: incorrectExtension });
                setUploadLimitExceeded({ uploadLimitExceeded: false });
            }
        }
        validateHandler(event.target);
        if (!event.target?.files[0]) {
            validateManaulHandler("partitionerCertificate", "");
        }
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsButtonDisabled(true);
        toasterContext.addToast({
            body: siteMessage("UpdateAccount_SuccessMessage"),
            messageType: "success",
            timeoutLength: 3500,
        });
        history.push("/MyAccount/UploadCredentialsSuccessful");
    };

    const submitHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        // if (!isSubmitted) {
        //     setIsSubmitted(true);
        // }

        const isReCaptchaValid = await validateReCaptcha({ location: "", recaptchaRef });

        // const isReCaptchaValid = await validateReCaptcha({});
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            return;
        }

        await updateAccount({
            phoneNumber,
            practiceName,
            practiceType,
            address1,
            address2,
            address3,
            taxIdEin,
            currentDba,
            taxCertificate: taxFile,
            practitionerCertificate: partitionerLicenseFile,
            state: selectedState?.abbreviation || otherState,
            country: selectedCountry?.abbreviation,
            city,
            postalCode,
            isShipAddress,
            shipAddress1,
            shipAddress2,
            shipAddress3,
            shipState: shipSelectedState?.abbreviation || shipOtherState,
            shipCountry: shipSelectedCountry?.abbreviation,
            shipCity,
            shipPostalCode,
            onSuccess: onSuccessHandler,
            onError: error => {
                setIsButtonDisabled(true);
                setErrorMessage(error);
                setIsSubmitting(false);
                setInValidFile(true);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        // For phase 1 some fields were not required so they are commented below
        <GAIASignInRequiredToBrowseWrapper>
            <StyledWrapper {...styles.form}>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.userGridItem}>
                        <GridContainer {...styles.userGridContainer}>
                            <GridItem {...styles.fullWidthGridItem}>
                                <Typography {...styles.heading}>{translate("My Credentials")}</Typography>
                            </GridItem>

                            <GridItem {...styles.uploadTaxGridItem}>
                                <FileUpload
                                    {...styles.fileUploader}
                                    accept=".pdf,.jpg,.png"
                                    label={translate("Upload Tax Resale Certificate")}
                                    inputRef={fileUploadTaxCertificateRef}
                                    fileName={taxFile?.name || ""}
                                    error={
                                        (incorrectFileExtension && translate("must be .png, .jpg or .pdf")) ||
                                        (taxSizeExceeded && translate("file size must not exceed 5MB"))
                                    }
                                    onFileChange={taxFileChangeHandler}
                                />
                                <StyledWrapper {...styles.noteWrapper}>
                                    <Typography {...styles.noteBold}>Note:</Typography>
                                    <Typography>
                                        Tax will be applicable on your purchases if tax certificate is not uploaded
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.uploadLicenseGridItem}>
                                <FileUpload
                                    {...styles.fileUploader}
                                    name="partitionerCertificate"
                                    accept=".pdf,.jpg,.png"
                                    label="Upload Practitioner Credentials"
                                    fileName={partitionerLicenseFile?.name || ""}
                                    inputRef={fileUploadPartitionerLicenceRef}
                                    error={
                                        (((!!formErrors.partitionerCertificate && formErrors.partitionerCertificate) ||
                                            incorrectExtension) &&
                                            translate("must be .png, .jpg or .pdf")) ||
                                        (fileSizeExceeded && translate("filesize must not exceed 5MB"))
                                    }
                                    onFileChange={credentialsFileChangeHandler}
                                    onBlur={e => validateHandler(e.currentTarget)}
                                    required
                                />
                                <StyledWrapper {...styles.noteWrapper}>
                                    <Typography {...styles.noteBold}>Note:</Typography>
                                    <Typography>
                                        To activate your account, please upload your credentials or send it on our email
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.indentationGridItem}>{/* {Indentation} */}</GridItem>
                            {billToState.value?.customerNumber === certificateUploadForDefaultCustomer && (
                                <>
                                    <GridItem {...styles.fullWidthGridItem}>
                                        <Typography {...styles.heading}>{translate("My Account Details")}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.lastNameTextField}
                                            label={translate("Practice Name")}
                                            name="practiceName"
                                            value={practiceName}
                                            data-test-selector="createAccount_practiceName"
                                            onChange={e => practiceNameChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.practiceName && formErrors.practiceName}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.practiceName ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter practice name"
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.practitionerTypeGridItem}>
                                        <Select
                                            {...styles.countryTextField}
                                            label="Practitioner Type"
                                            name="practiceType"
                                            onChange={practionerTypeChangeHandler}
                                            onBlur={(e: any) => validateHandler(e.currentTarget)}
                                            value={practiceType || ""}
                                            error={!!formErrors.practiceType && formErrors.practiceType}
                                            required
                                        >
                                            <option value="">{translate("Choose")}</option>
                                            {practiceTypeOptions.map(pType => (
                                                <option key={pType.value} value={pType.value}>
                                                    {pType.label}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.phoneNumberTextField}
                                            label={translate("Phone Number")}
                                            name="phoneNumber"
                                            value={phoneNumber}
                                            data-test-selector="createAccount_phoneNumber"
                                            onChange={e => phoneNumberChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.phoneNumber && formErrors.phoneNumber}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.phoneNumber ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Phone Number"
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}></GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.phoneNumberTextField}
                                            label="Tax ID/EIN"
                                            name="taxIdEin"
                                            value={taxIdEin}
                                            data-test-selector="createAccount_taxIdEin"
                                            onChange={e => taxIdEinChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.taxIdEin && formErrors.taxIdEin}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.taxIdEin ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Tax ID/EIN"
                                            required
                                        />
                                        <StyledWrapper {...styles.noteWrapper}>
                                            <Typography {...styles.noteBold}>Note:</Typography>
                                            <Typography>This information is required for wholesale accounts</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.phoneNumberTextField}
                                            label={translate("All Current DBA’s")}
                                            name="currentDba"
                                            value={currentDba}
                                            data-test-selector="createAccount_currentDba"
                                            onChange={e => currentDbaChangeHandler(e.currentTarget)}
                                            placeholder="Enter All Current DBA's"
                                        />
                                    </GridItem>

                                    <GridItem {...styles.fullWidthGridItem}>
                                        <Typography {...styles.generalHeading}>
                                            {translate("Billing Address")}
                                        </Typography>
                                    </GridItem>

                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.lastNameTextField}
                                            label={translate("Address Line 1")}
                                            name="address1"
                                            value={address1}
                                            data-test-selector="createAccount_Address1"
                                            onChange={e => address1ChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.address1 && formErrors.address1}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.address1 ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Address 1"
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.lastNameTextField}
                                            label={translate("Address Line 2")}
                                            name="address2"
                                            value={address2}
                                            data-test-selector="createAccount_Address2"
                                            onChange={e => address2ChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.address2 && formErrors.address2}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.address2 ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Address 2"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.lastNameTextField}
                                            label={translate("Address Line 3")}
                                            name="address3"
                                            value={address3}
                                            data-test-selector="createAccount_Address3"
                                            onChange={e => address3ChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.address3 && formErrors.address3}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.address3 ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Address 3"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}></GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.phoneNumberTextField}
                                            label="city"
                                            name="city"
                                            value={city}
                                            data-test-selector="createAccount_city"
                                            onChange={e => cityChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.city && formErrors.city}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.city ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter city"
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.countryGridItem}>
                                        <Select
                                            {...styles.countryTextField}
                                            label="Country"
                                            name="country"
                                            onChange={handleCountryChange}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            value={selectedCountry?.id || ""}
                                            error={!!formErrors.country && formErrors.country}
                                            labelPosition="left"
                                            required
                                        >
                                            <option value="">{translate("Select Country")}</option>
                                            {countries?.map(co => (
                                                <option key={co.id} value={co.id}>
                                                    {co.name}
                                                </option>
                                            ))}
                                        </Select>
                                        {selectedCountry && selectedCountry?.abbreviation !== "US" ? (
                                            <StyledWrapper {...styles.alertContainer}>
                                                <Typography>
                                                    <AlertTriangle />
                                                </Typography>
                                                <Typography {...styles.alertText}>
                                                    Health Care Professionals interested in website access, outside US
                                                    cannot order directly.
                                                </Typography>
                                            </StyledWrapper>
                                        ) : null}
                                    </GridItem>
                                    <GridItem {...styles.stateGridItem}>
                                        {selectedCountry?.states?.length === 0 ? (
                                            <TextField
                                                {...styles.phoneNumberTextField}
                                                label={translate("State/Province")}
                                                name="state"
                                                onChange={e => otherStateChangeHandler(e.currentTarget)}
                                                onBlur={e => validateHandler(e.currentTarget)}
                                                error={
                                                    // eslint-disable-next-line no-extra-boolean-cast
                                                    !!formErrors.state
                                                        ? formErrors.state
                                                        : stateValidationError
                                                        ? translate("Please Enter valid state")
                                                        : undefined
                                                }
                                                value={otherState}
                                                data-test-selector="createAccount_firstName"
                                                placeholder="Enter state/Province"
                                                required
                                            />
                                        ) : (
                                            <Select
                                                {...styles.stateTextField}
                                                label="State/Province"
                                                name="state"
                                                value={selectedState?.id || ""}
                                                onChange={handleStateChange}
                                                onBlur={e => validateHandler(e.currentTarget)}
                                                error={!!formErrors.state && formErrors.state}
                                                labelPosition="left"
                                                required
                                            >
                                                <option value="">{translate("-Select state/Province-")}</option>
                                                {selectedCountry?.states?.map(st => (
                                                    <option key={st.id} value={st.id}>
                                                        {st.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.GridItemWidth}>
                                        <TextField
                                            {...styles.phoneNumberTextField}
                                            label="Postal Code"
                                            name="postalCode"
                                            value={postalCode}
                                            data-test-selector="createAccount_postalCode"
                                            onChange={e => postalCodeChangeHandler(e.currentTarget)}
                                            onBlur={e => validateHandler(e.currentTarget)}
                                            error={!!formErrors.postalCode && formErrors.postalCode}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.postalCode ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Postal Code"
                                            required
                                        />
                                    </GridItem>

                                    {/* Shipping Address Starts */}
                                    <GridItem {...styles.shipTitleGridItem}>
                                        {!isShipAddress ? (
                                            <Typography onClick={handleisShipAddress} {...styles.addIconWrapper}>
                                                <Typography {...styles.addIcon}>
                                                    <Plus />
                                                </Typography>
                                                <Typography {...styles.redLinks}>
                                                    {translate("Add Shipping Address")}
                                                </Typography>
                                            </Typography>
                                        ) : (
                                            <Typography {...styles.headingWrapper}>
                                                <Typography {...styles.generalHeading}>
                                                    {translate("Shipping Address")}
                                                </Typography>
                                                <Typography onClick={handleisShipAddress} {...styles.redLinks}>
                                                    {translate("Remove")}
                                                </Typography>
                                            </Typography>
                                        )}
                                    </GridItem>
                                    {isShipAddress && (
                                        <>
                                            <GridItem {...styles.rightSectionGridItem}>
                                                <TextField
                                                    {...styles.lastNameTextField}
                                                    label={translate("Address Line 1")}
                                                    name="shipAddress1"
                                                    value={shipAddress1}
                                                    data-test-selector="createAccount_Address1"
                                                    onChange={e => shipAddress1ChangeHandler(e.currentTarget)}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    error={!!formErrors.shipAddress1 && formErrors.shipAddress1}
                                                    iconProps={{
                                                        color: "danger",
                                                        src: formErrors.shipAddress1 ? AlertTriangle : undefined,
                                                    }}
                                                    placeholder="Enter Address 1"
                                                    required
                                                />
                                            </GridItem>
                                            <GridItem {...styles.rightSectionGridItem}>
                                                <TextField
                                                    {...styles.lastNameTextField}
                                                    label={translate("Address Line 2")}
                                                    name="shipAddress2"
                                                    value={shipAddress2}
                                                    data-test-selector="createAccount_Address2"
                                                    onChange={e => shipAddress2ChangeHandler(e.currentTarget)}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    error={!!formErrors.shipAddress2 && formErrors.shipAddress2}
                                                    iconProps={{
                                                        color: "danger",
                                                        src: formErrors.shipAddress2 ? AlertTriangle : undefined,
                                                    }}
                                                    placeholder="Enter Address 2"
                                                />
                                            </GridItem>
                                            <GridItem {...styles.rightSectionGridItem}>
                                                <TextField
                                                    {...styles.lastNameTextField}
                                                    label={translate("Address Line 3")}
                                                    name="shipAddress3"
                                                    value={shipAddress3}
                                                    data-test-selector="createAccount_Address3"
                                                    onChange={e => shipAddress3ChangeHandler(e.currentTarget)}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    error={!!formErrors.shipAddress3 && formErrors.shipAddress3}
                                                    iconProps={{
                                                        color: "danger",
                                                        src: formErrors.shipAddress3 ? AlertTriangle : undefined,
                                                    }}
                                                    placeholder="Enter Address 3"
                                                />
                                            </GridItem>

                                            <GridItem {...styles.GridItemWidth}></GridItem>
                                            <GridItem {...styles.extGridItem}>
                                                <TextField
                                                    {...styles.phoneNumberTextField}
                                                    label={translate("city")}
                                                    name="shipCity"
                                                    value={shipCity}
                                                    data-test-selector="createAccount_city"
                                                    onChange={e => shipCityChangeHandler(e.currentTarget)}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    error={!!formErrors.shipCity && formErrors.shipCity}
                                                    iconProps={{
                                                        color: "danger",
                                                        src: formErrors.shipCity ? AlertTriangle : undefined,
                                                    }}
                                                    placeholder="Enter city"
                                                    required
                                                />
                                            </GridItem>

                                            <GridItem {...styles.countryGridItem}>
                                                <Select
                                                    {...styles.countryTextField}
                                                    label="Country"
                                                    name="shipCountry"
                                                    onChange={handleShipCountryChange}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    value={shipSelectedCountry?.id || ""}
                                                    error={!!formErrors.shipCountry && formErrors.shipCountry}
                                                    labelPosition="left"
                                                    required
                                                >
                                                    <option value="">{translate("Select Country")}</option>
                                                    {countries?.map(co => (
                                                        <option key={co.id} value={co.id}>
                                                            {co.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                                {shipSelectedCountry && shipSelectedCountry?.abbreviation !== "US" ? (
                                                    <StyledWrapper {...styles.alertContainer}>
                                                        <Typography>
                                                            <AlertTriangle />
                                                        </Typography>
                                                        <Typography {...styles.alertText}>
                                                            Health Care Professionals interested in website access,
                                                            outside US cannot order directly.
                                                        </Typography>
                                                    </StyledWrapper>
                                                ) : null}
                                            </GridItem>
                                            <GridItem {...styles.stateGridItem}>
                                                {shipSelectedCountry?.states?.length === 0 ? (
                                                    <TextField
                                                        {...styles.phoneNumberTextField}
                                                        label={translate("State/Province")}
                                                        name="shipState"
                                                        onChange={e => shipOtherStateChangeHandler(e.currentTarget)}
                                                        onBlur={e => validateHandler(e.currentTarget)}
                                                        error={
                                                            // eslint-disable-next-line no-extra-boolean-cast
                                                            !!formErrors.shipState
                                                                ? formErrors.shipState
                                                                : stateValidationError
                                                                ? translate("Please Enter valid state")
                                                                : undefined
                                                        }
                                                        value={shipOtherState}
                                                        data-test-selector="createAccount_firstName"
                                                        placeholder="Enter state/Province"
                                                        required
                                                    />
                                                ) : (
                                                    <Select
                                                        {...styles.stateTextField}
                                                        label="State/Province"
                                                        name="shipState"
                                                        value={shipSelectedState?.id || ""}
                                                        onChange={handleShipStateChange}
                                                        onBlur={e => validateHandler(e.currentTarget)}
                                                        error={!!formErrors.shipState && formErrors.shipState}
                                                        labelPosition="left"
                                                        required
                                                    >
                                                        <option value="">{translate("-Select state/Province-")}</option>
                                                        {shipSelectedCountry?.states?.map(st => (
                                                            <option key={st.id} value={st.id}>
                                                                {st.name}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                )}
                                            </GridItem>

                                            <GridItem {...styles.phoneNumberGridItem}>
                                                <TextField
                                                    {...styles.phoneNumberTextField}
                                                    label={translate("Postal Code")}
                                                    name="shipPostalCode"
                                                    value={shipPostalCode}
                                                    data-test-selector="createAccount_postalCode"
                                                    onChange={e => shipPostalCodeChangeHandler(e.currentTarget)}
                                                    onBlur={e => validateHandler(e.currentTarget)}
                                                    error={!!formErrors.shipPostalCode && formErrors.shipPostalCode}
                                                    iconProps={{
                                                        color: "danger",
                                                        src: formErrors.shipPostalCode ? AlertTriangle : undefined,
                                                    }}
                                                    placeholder="Enter Postal Code"
                                                    required
                                                />
                                            </GridItem>
                                        </>
                                    )}
                                    {/* Shipping End */}
                                </>
                            )}

                            <GridItem {...styles.fullWidthGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isButtonDisabled || inValidFile || taxSizeExceeded || fileSizeExceeded}
                                    onClick={submitHandler}
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Update")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </GAIASignInRequiredToBrowseWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(UpdateHCPAccountDetail)),
    definition: {
        group: "My Account",
        displayName: "Update Account",
    },
};

export default widgetModule;
